import React, { useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Popover, OverlayTrigger, Row, Stack } from '../ui-kit';
import { IconCaretRight, IconMobile, IconBriefcase, IconHouse, IconPeopleGroup, IconPhone } from '../ui-kit/icons';

import AvailabilityIcon from './AvailabilityIcon';
import GroupName from './GroupName';
import {
  SendMessageButton,
  UserContractButton,
  ScheduleButton,
  LoginAsButton,
  UserPagersButton,
  UserMobileDevicesButton
} from '../../utils/UserActions';
import Loader from '../general/Loader';
import RankIcon from '../general/RankIcon';

import SkillIconList from './SkillIconList';

import { operations as authOperations, selectors as authSelectors } from '../../state/modules/authorization';

const phoneTypeIcon = phone => {
  const phoneTypes = {
    mobile: IconMobile,
    work: IconBriefcase,
    home: IconHouse,
    ice: IconPeopleGroup,
    other: IconPhone
  };

  const Icon = phoneTypes[phone.phone_type] || IconPhone;
  const title = I18n.t(`classy_enum.phone_type.${phone.phone_type}`);
  return <Icon fixedWidth title={title} />;
};

const actionButtons = {
  login_as: LoginAsButton,
  message: SendMessageButton,
  contracts: UserContractButton,
  schedule: ScheduleButton,
  pagers: UserPagersButton,
  mobile_devices: UserMobileDevicesButton
};

const paddingLevels = {
  region: 0,
  cluster: 2,
  station: 4,
  team: 5
};

const TreeElement = ({ data }) => (
  <div style={{ position: 'relative' }}>
    <div className={`ps-${paddingLevels[data.type]}`}>
      <div>
        <a href={`/groups/${data.id}`}>
          <IconCaretRight /> {data.type === 'team' && <GroupName group={data} fullName={true} />}
          {data.type !== 'team' && data.name}
        </a>{' '}
        {data.rank && <RankIcon rank={data.rank} />}
      </div>
    </div>
  </div>
);

const UserActions = ({ user, membership, permittedActions }) => (
  <Row className="pb-3">
    <Stack direction="horizontal" gap={1}>
      {permittedActions.map((action, index) => {
        const ActionButton = actionButtons[action];
        return (
          <React.Fragment key={action}>
            <ActionButton user={user} membership={membership} />
            {index === permittedActions.length - 1 || <div className="vr" />}
          </React.Fragment>
        );
      })}
    </Stack>
  </Row>
);

const UserDetails = ({ user, skills, showId }) => (
  <>
    {showId && <div>{user.id}</div>}
    {user.personnel_number && (
      <div>
        {I18n.t('activerecord.attributes.user.personnel_number')}: <b>{user.personnel_number}</b>
      </div>
    )}
    <RankIcon rank={user.rank} />
    <div>
      <span>{user.nickname}</span> <SkillIconList skills={skills} />
    </div>
    {user.phone_numbers.map(phone => (
      <div key={phone.phone_number}>
        {phoneTypeIcon(phone)} {phone.phone_number}
      </div>
    ))}
    {user.email && (
      <div className={'pt-3'}>
        <a href={`mailto:${user.email}`}>
          <strong>{user.email}</strong>
        </a>
      </div>
    )}
  </>
);

const PopoverContent = ({ user, loading, membership, groups, permittedActions }) => {
  if (loading || !groups) return <Loader />;

  const admin = permittedActions['login_as'] === true;

  return (
    <>
      <UserActions user={user} membership={membership} permittedActions={permittedActions} />
      <UserDetails user={user} skills={user.skills} showId={admin} />
      {groups.map(group => (
        <TreeElement data={group.data} level={group.level} key={group.data.id} />
      ))}
    </>
  );
};

const UserPopover = ({
  displayFullName,
  user,
  groupId,
  tree,
  loadDetails,
  detailsLoaded,
  userPermittedActions,
  fetchPermissions
}) => {
  user = user || {};

  const displayName = displayFullName ? user.full_name : user.nickname;

  const userProfileUrl = groupId ? `/groups/${groupId}/users/${user.id}` : `/users/${user.id}`;

  const permissionQueries = {
    queries: [
      { action: 'become', object_type: 'User', id: user.id },
      { action: 'schedule', object_type: 'User', id: user.id },
      { action: 'read', object_type: 'Contract', user_id: user.id },
      { action: 'read', object_type: 'Pager', user_id: user.id },
      { action: 'read', object_type: 'MobileDevice', user_id: user.id }
    ]
  };

  const membership = useMemo(() => user?.memberships?.find(m => m.group_id === groupId), [user, groupId]);

  const groupAvailability = useMemo(() => membership && membership.availability, [membership]);

  // This method receives a group tree, where each node has the group data and
  // and an array of children nodes. The method appends the user's membership
  // and ranks pertraining each group of the tree and flattens it into an array
  // that will be used to display the tree items
  const generateGroupListItemsFromTree = useCallback(
    (groups = tree, memberships = user?.memberships, level = 0, arr = []) => {
      if (!memberships) return [];
      if (!groups) return [];

      // Tree structure: { data: { ... }, children: [ { data: { ... }, children: [...]}, ... ]}
      // Ouptput structure: [ { data: {...}, level: 0 }, ... ]
      for (const [id, { data, children }] of Object.entries(groups)) {
        const membership = memberships.find(membership => membership.group_id === Number(id));
        const groupData = { ...data };

        if (membership) {
          groupData.membership = membership.id;
          groupData.rank = membership.override_rank;
        }

        if (membership || groupData.type === 'cluster' || groupData.type === 'region')
          arr.push({ data: groupData, level });

        arr = generateGroupListItemsFromTree(children, memberships, level + 1, arr);
      }

      return arr;
    },
    [tree, user]
  );

  // This method is in charge of generating the group items tree, then fitering the ones
  // that should be displayed.
  const groupListItems = useMemo(() => {
    let value = generateGroupListItemsFromTree();

    let groupsToDisplay = value.filter(({ data }) => {
      // If there's a membership for said group it should be displayed
      if (data.membership) return true;

      // If the item itself doesn't have a membership, but any of it's descendants has a
      // membership, we should display it too.
      const descendantIds = data.descendant_ids || [];
      const descendants = value.filter(treeItem => descendantIds.includes(treeItem.data.id));
      return descendants.find(treeItem => !!treeItem.data.membership);
    });

    return groupsToDisplay;
  }, [tree, user]);

  const permittedActions = useMemo(() => userPermittedActions(user?.id), [user?.id, userPermittedActions]);

  const loadUser = () => {
    loadDetails();
    fetchPermissions(permissionQueries);
  };

  return (
    <OverlayTrigger
      trigger={['click']}
      placement="right"
      onEntered={loadUser}
      rootClose={true}
      overlay={
        <Popover id="popover-basic" className={`user-info-popover-${user?.id}`}>
          <Popover.Header>
            <h3 className="m-0">
              <a href={userProfileUrl}>{user?.full_name}</a>{' '}
              <span className={'float-end'}>
                {groupAvailability && <AvailabilityIcon {...groupAvailability} withIcon={true} />}
              </span>
            </h3>
          </Popover.Header>
          <Popover.Body>
            {detailsLoaded && (
              <PopoverContent
                loading={!detailsLoaded}
                user={user}
                membership={membership}
                groups={groupListItems}
                permittedActions={permittedActions}
              />
            )}
          </Popover.Body>
        </Popover>
      }
    >
      <span className="user-popup-trigger">{displayName}</span>
    </OverlayTrigger>
  );
};

const mapStateToProps = state => ({
  userPermittedActions: authSelectors.userPermittedActions(state)
});

const mapDispatchToProps = {
  fetchPermissions: authOperations.fetchPermissions
};

export default connect(mapStateToProps, mapDispatchToProps)(UserPopover);
